import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertTriangle, XCircle, FileWarning, Sparkles } from "lucide-react";
import PdfViewer from "@/components/PdfViewer";

const CATEGORY_LABELS = {
  identification: "Identificazione",
  units_scale: "Unità e scala",
  material: "Materiale e quantità",
  geometry: "Geometria",
  tolerances_finish: "Tolleranze e finitura",
  bending: "Piegatura",
  welding_treatments: "Saldature e trattamenti",
  references: "Riferimenti",
};

const CATEGORIES = {
  identification: ["drawing_number", "drawing_title", "revision", "page_number", "total_pages", "component_description", "part_number"],
  units_scale: ["units", "scale"],
  material: ["material", "material_grade", "thickness_mm", "quantity", "declared_weight_kg"],
  geometry: ["overall_dimensions", "diameters", "radii", "angles", "holes", "slots", "countersinks", "counterbores", "threads"],
  tolerances_finish: ["dimensional_tolerances", "geometric_tolerances", "surface_roughness"],
  bending: ["bend_count", "bend_angles", "bend_radii"],
  welding_treatments: ["welding_notes", "heat_treatments", "surface_treatments", "finishes"],
  references: ["standards_references", "production_notes", "external_processes"],
};

const MANDATORY = ["units", "material", "thickness_mm", "quantity", "geometry_or_dimensions"];

function statusChip(status) {
  const map = {
    extracted: { c: "yellow", label: "estratto" },
    inferred: { c: "yellow", label: "inferito" },
    user_confirmed: { c: "green", label: "confermato" },
    unavailable: { c: "red", label: "mancante" },
    conflict: { c: "red", label: "conflitto" },
  };
  const s = map[status] || { c: "red", label: status };
  return <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border bg-reliability-${s.c}`}>{s.label}</span>;
}

function originChip(origin) {
  if (!origin) return null;
  return <span className="text-[9px] uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5 ml-1">{origin.replace("_", " ")}</span>;
}

function fmtVal(v, unit) {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.join(unit ? ` × ` : ", ") + (unit ? ` ${unit}` : "");
  if (typeof v === "boolean") return v ? "sì" : "no";
  return unit ? `${v} ${unit}` : String(v);
}

export default function PdfAnalysisPanel({ jobId, rfqId, onHandoff }) {
  const [job, setJob] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/pdf-jobs/${jobId}`);
      setJob(data);
      const seed = {};
      Object.entries(data.result?.fields || {}).forEach(([k, f]) => {
        if (["extracted", "user_confirmed", "conflict"].includes(f.status)) {
          seed[k] = { value: f.value, unit: f.unit };
        }
      });
      setEdits(seed);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento");
    }
  };
  useEffect(() => { if (jobId) load(); }, [jobId]);

  const runVision = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/pdf-jobs/${job.file_id}/analyze`, { use_vision: true });
      toast.success("Analisi vision completata");
      // Backend returns a NEW job when use_vision differs
      if (data.job_id && data.job_id !== jobId && onHandoff) onHandoff({ newJobId: data.job_id });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore vision");
    } finally { setRunning(false); }
  };

  const confirmField = async (key) => {
    setSaving(true);
    try {
      const patch = edits[key] || {};
      await api.put(`/pdf-jobs/${jobId}/confirm`, { confirmations: { [key]: patch } });
      toast.success(`Campo confermato: ${key}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore conferma");
    } finally { setSaving(false); }
  };

  const confirmAll = async () => {
    setSaving(true);
    try {
      const confirmations = {};
      Object.entries(edits).forEach(([k, v]) => {
        if (v && v.value !== "" && v.value !== null && v.value !== undefined) {
          confirmations[k] = v;
        }
      });
      await api.put(`/pdf-jobs/${jobId}/confirm`, { confirmations });
      toast.success("Campi confermati");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore conferma");
    } finally { setSaving(false); }
  };

  const handoff = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/pdf-jobs/${jobId}/handoff`);
      toast.success("Dati trasferiti all'RFQ");
      if (onHandoff) onHandoff({ handedOff: true, manual_inputs: data.manual_inputs });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "PDF_INSUFFICIENT_DATA") {
        toast.error(`Dati insufficienti: ${(detail.missing || []).join(", ")}`);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Errore trasferimento");
      }
    } finally { setSaving(false); }
  };

  const summary = useMemo(() => {
    if (!job) return null;
    const cl = job.result?.document?.classification || "unknown";
    const map = { vector: "Vettoriale", scanned: "Scansionato", hybrid: "Ibrido", unknown: "Sconosciuto" };
    return { classification: map[cl] || cl, insufficient: job.result?.insufficient_data, missing: job.result?.missing_mandatory || [] };
  }, [job]);

  if (!job) return null;
  const totalPages = job.result?.document?.page_count || 1;
  const fields = job.result?.fields || {};

  return (
    <section className="border border-border bg-white" data-testid="pdf-analysis-panel">
      <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Lettore PDF tecnico · beta</div>
          <div className="font-display font-bold text-sm">Job {jobId.slice(-8)}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 border text-[10px] uppercase tracking-widest bg-reliability-${summary.classification === "Vettoriale" ? "green" : (summary.classification === "Ibrido" ? "yellow" : "yellow")}`}>{summary.classification}</span>
          {summary.insufficient
            ? <span className="px-2 py-1 border text-[10px] uppercase tracking-widest bg-reliability-red flex items-center gap-1"><FileWarning size={12} /> dati insufficienti</span>
            : <span className="px-2 py-1 border text-[10px] uppercase tracking-widest bg-reliability-green flex items-center gap-1"><CheckCircle2 size={12} /> pronto per handoff</span>}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={running || job.used_vision} onClick={runVision} data-testid="btn-pdf-vision">
            <Sparkles size={14} className="mr-1" /> {job.used_vision ? "Vision già usata" : (running ? "Analisi vision…" : "Analisi vision (AI)")}
          </Button>
          <Button size="sm" disabled={saving || summary.insufficient} onClick={handoff} data-testid="btn-pdf-handoff">
            Trasferisci a RFQ
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 p-4">
        <PdfViewer jobId={jobId} totalPages={totalPages} />

        <div>
          <div className="text-[11px] text-muted-foreground bg-muted p-2 border border-border mb-3">
            Il lettore PDF <b>non</b> sostituisce un file CAD e <b>non</b> inventa quote mancanti. Ogni valore mostra origine e affidabilità: conferma tu i campi prima del trasferimento al preventivatore.
          </div>

          {summary.missing.length > 0 && (
            <div className="mb-3 border border-destructive/40 bg-reliability-red p-3" data-testid="pdf-missing-list">
              <div className="text-[10px] uppercase tracking-widest flex items-center gap-1 mb-1"><AlertTriangle size={12} /> Dati indispensabili mancanti</div>
              <ul className="text-xs list-disc pl-4">
                {summary.missing.map(m => <li key={m}>{m}</li>)}
              </ul>
            </div>
          )}

          <div className="max-h-[540px] overflow-auto pr-1">
            {Object.entries(CATEGORIES).map(([cat, keys]) => (
              <div key={cat} className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border pb-1 mb-2">{CATEGORY_LABELS[cat]}</div>
                <div className="grid grid-cols-1 gap-2">
                  {keys.map(k => {
                    const f = fields[k] || { value: null, unit: null, origin: null, status: "unavailable", confidence: 0, page: null };
                    const isMandatory = MANDATORY.includes(k) || (k === "overall_dimensions" && MANDATORY.includes("geometry_or_dimensions"));
                    return (
                      <div key={k} className="border border-border p-2 text-xs" data-testid={`pdf-field-${k}`}>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <div className="font-mono font-bold">{k}</div>
                          {statusChip(f.status)}
                          {originChip(f.origin)}
                          {f.confidence != null && f.confidence > 0 && (
                            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">conf {(f.confidence * 100).toFixed(0)}%</span>
                          )}
                          {f.page && <span className="text-[9px] uppercase tracking-widest text-muted-foreground">p{f.page}</span>}
                          {isMandatory && <span className="text-[9px] uppercase tracking-widest text-destructive">obbligatorio</span>}
                        </div>
                        <div className="grid grid-cols-3 gap-2 items-end">
                          <div className="col-span-2">
                            <Label className="text-[10px]">Valore</Label>
                            <Input
                              data-testid={`pdf-field-input-${k}`}
                              value={edits[k]?.value ?? (Array.isArray(f.value) ? f.value.join(",") : (f.value ?? ""))}
                              onChange={(e) => {
                                let v = e.target.value;
                                if (Array.isArray(f.value)) v = v.split(",").map(s => s.trim()).filter(Boolean).map(Number).filter(x => !Number.isNaN(x));
                                setEdits({ ...edits, [k]: { ...(edits[k] || {}), value: v, unit: edits[k]?.unit ?? f.unit } });
                              }}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px]">Unità</Label>
                            <Input
                              data-testid={`pdf-field-unit-${k}`}
                              value={edits[k]?.unit ?? f.unit ?? ""}
                              onChange={(e) => setEdits({ ...edits, [k]: { ...(edits[k] || {}), unit: e.target.value, value: edits[k]?.value ?? f.value } })}
                            />
                          </div>
                        </div>
                        {f.evidence?.text && <div className="mt-1 text-[10px] text-muted-foreground italic">« {f.evidence.text.slice(0, 120)} »</div>}
                        {f.conflict && (
                          <div className="mt-1 text-[10px] text-destructive">
                            Conflitto: {String(f.conflict.value_a)} ({f.conflict.origin_a}) vs {String(f.conflict.value_b)} ({f.conflict.origin_b})
                          </div>
                        )}
                        {f.notes && <div className="mt-1 text-[10px] text-muted-foreground">{f.notes}</div>}
                        <div className="mt-2 flex justify-end">
                          <Button size="sm" variant="outline" onClick={() => confirmField(k)} disabled={saving} data-testid={`pdf-confirm-${k}`}>
                            Conferma
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={confirmAll} disabled={saving} data-testid="btn-pdf-confirm-all">Conferma tutti i campi</Button>
            <Button size="sm" variant="outline" onClick={load} disabled={saving} data-testid="btn-pdf-reload">Ricarica</Button>
          </div>
        </div>
      </div>
    </section>
  );
}
